package com.gloopgo.game.engine

import kotlin.math.ln
import kotlin.math.max
import kotlin.math.sqrt

/**
 * Static food pellet scattered across the map.
 */
data class FoodPellet(
    val id: String,
    val position: Vector2D,
    val color: Long,
    val mass: Float = GameConstants.PELLET_MASS,
    val emoji: String = ""
) {
    val isHazard: Boolean
        get() = mass < 0f || GameConstants.isHazardEmoji(emoji)

    val effectiveMass: Float
        get() = if (isHazard) GameConstants.HAZARD_PELLET_MASS else mass

    val radius: Float
        get() = if (isHazard) {
            GameConstants.HAZARD_PELLET_RADIUS
        } else {
            GameConstants.PELLET_BASE_RADIUS + (mass - 1f) * GameConstants.PELLET_RADIUS_MASS_SCALE
        }

    fun getFoodEmoji(): String {
        return if (emoji.isNotEmpty()) emoji else GameConstants.getFoodEmojiForId(id)
    }
}

/**
 * Ejected mass blob traveling with decaying momentum.
 */
data class EjectedMass(
    val id: String,
    val ownerId: String,
    val position: Vector2D,
    val velocity: Vector2D,
    val color: Long,
    val mass: Float = GameConstants.EJECTED_MASS_VALUE,
    val createdAt: Long = 0L
) {
    val radius: Float
        get() = sqrt(mass * GameConstants.MASS_TO_RADIUS_FACTOR)
}

/**
 * An individual cell belonging to a player (a player can control 1 to 16 split cells).
 */
data class GloopCell(
    val id: String,
    val position: Vector2D,
    val velocity: Vector2D = Vector2D.ZERO,
    val mass: Float = GameConstants.INITIAL_MASS,
    val canRecombineAt: Long = 0L,
    val targetPosition: Vector2D = position,
    val targetMass: Float = mass
) {
    val radius: Float
        get() = sqrt(mass * GameConstants.MASS_TO_RADIUS_FACTOR)

    val speed: Float
        get() = max(
            GameConstants.MIN_SPEED,
            GameConstants.BASE_SPEED / (GameConstants.SPEED_LOG_OFFSET + ln(max(1f, mass)))
        )
}

/**
 * Represents a player or bot entity containing one or more cells.
 */
data class GloopPlayer(
    val id: String,
    val name: String,
    val color: Long,
    val cells: List<GloopCell>,
    val isAlive: Boolean = true,
    val emoji: String = "",
    val lastHeartbeat: Long = 0L,
    val joinedAt: Long = 0L
) {
    val totalMass: Float
        get() = cells.sumOf { it.mass.toDouble() }.toFloat()

    val canSplit: Boolean
        get() = isAlive &&
            cells.size < GameConstants.MAX_CELLS_PER_PLAYER &&
            cells.any { it.mass >= GameConstants.SPLIT_MIN_MASS }

    val canEjectMass: Boolean
        get() = isAlive &&
            cells.any { it.mass > GameConstants.EJECT_MIN_MASS }

    /**
     * Center of gravity (mass-weighted average position of all split cells).
     */
    val centerOfGravity: Vector2D
        get() {
            if (cells.isEmpty()) return Vector2D.ZERO
            val totalM = totalMass
            if (totalM <= 0.001f) return cells.first().position
            val weightedX = cells.sumOf { (it.position.x * it.mass).toDouble() }.toFloat()
            val weightedY = cells.sumOf { (it.position.y * it.mass).toDouble() }.toFloat()
            return Vector2D(weightedX / totalM, weightedY / totalM)
        }

    val centerPosition: Vector2D
        get() = centerOfGravity

    val maxRadius: Float
        get() = cells.maxOfOrNull { it.radius } ?: 0f

    /**
     * Bounding radius enclosing all split cells from the center of gravity.
     */
    val boundingRadius: Float
        get() {
            if (cells.isEmpty()) return 0f
            val cog = centerOfGravity
            return cells.maxOf { it.position.distanceTo(cog) + it.radius }
        }
}

/**
 * Snapshot of the current game frame for rendering and state observation.
 */
data class GameState(
    val localPlayer: GloopPlayer?,
    val remotePlayers: List<GloopPlayer> = emptyList(),
    val pellets: List<FoodPellet> = emptyList(),
    val ejectedMasses: List<EjectedMass> = emptyList(),
    val isGameOver: Boolean = false,
    val leaderboard: List<LeaderboardEntry> = emptyList(),
    val cameraTarget: Vector2D = Vector2D.ZERO,
    val cameraZoom: Float = 1.0f,
    val splitCooldownRemainingMs: Long = 0L,
    val ejectCooldownRemainingMs: Long = 0L
)

/**
 * Scoreboard entry for the in-game HUD.
 */
data class LeaderboardEntry(
    val id: String,
    val name: String,
    val score: Int,
    val isLocalPlayer: Boolean = false
)

/**
 * Event generated when a player cell consumes another player's cell.
 */
data class PredationEvent(
    val eaterPlayerId: String,
    val eaterCellId: String,
    val victimPlayerId: String,
    val victimCellId: String,
    val isLastCell: Boolean,
    val massGained: Float
)

/**
 * Animal skin / avatar option for customizing the player's circle.
 */
data class AnimalSkinOption(
    val id: String,
    val name: String,
    val emoji: String,
    val color: Long
)

val ANIMAL_SKIN_OPTIONS = listOf(
    AnimalSkinOption(id = "dragon", name = "Dragon", emoji = "🐲", color = 0xFFFF1744L),
    AnimalSkinOption(id = "drake", name = "Drake", emoji = "🐉", color = 0xFF10B981L),
    AnimalSkinOption(id = "trex", name = "T-Rex", emoji = "🦖", color = 0xFF22C55EL),
    AnimalSkinOption(id = "sauropod", name = "Dino", emoji = "🦕", color = 0xFF06B6D4L),
    AnimalSkinOption(id = "shark", name = "Shark", emoji = "🦈", color = 0xFF0284C7L),
    AnimalSkinOption(id = "dolphin", name = "Dolphin", emoji = "🐬", color = 0xFF00E5FFL),
    AnimalSkinOption(id = "whale", name = "Whale", emoji = "🐳", color = 0xFF3B82F6L),
    AnimalSkinOption(id = "octopus", name = "Octopus", emoji = "🐙", color = 0xFFD500F9L),
    AnimalSkinOption(id = "squid", name = "Squid", emoji = "🦑", color = 0xFFE040FBL),
    AnimalSkinOption(id = "crab", name = "Crab", emoji = "🦀", color = 0xFFFF5252L),
    AnimalSkinOption(id = "lion", name = "Lion", emoji = "🦁", color = 0xFFFFD600L),
    AnimalSkinOption(id = "tiger", name = "Tiger", emoji = "🐯", color = 0xFFFF3D00L),
    AnimalSkinOption(id = "leopard", name = "Leopard", emoji = "🐆", color = 0xFFF59E0BL),
    AnimalSkinOption(id = "fox", name = "Fox", emoji = "🦊", color = 0xFFFF6D00L),
    AnimalSkinOption(id = "wolf", name = "Wolf", emoji = "🐺", color = 0xFF818CF8L),
    AnimalSkinOption(id = "bear", name = "Bear", emoji = "🐻", color = 0xFFA1887FL),
    AnimalSkinOption(id = "panda", name = "Panda", emoji = "🐼", color = 0xFF38BDF8L),
    AnimalSkinOption(id = "koala", name = "Koala", emoji = "🐨", color = 0xFF94A3B8L),
    AnimalSkinOption(id = "gorilla", name = "Gorilla", emoji = "🦍", color = 0xFF64748BL),
    AnimalSkinOption(id = "monkey", name = "Monkey", emoji = "🐵", color = 0xFFFFB300L),
    AnimalSkinOption(id = "elephant", name = "Elephant", emoji = "🐘", color = 0xFFA78BFAL),
    AnimalSkinOption(id = "rhino", name = "Rhino", emoji = "🦏", color = 0xFF78909CL),
    AnimalSkinOption(id = "hippo", name = "Hippo", emoji = "🦛", color = 0xFF8E8D8AL),
    AnimalSkinOption(id = "giraffe", name = "Giraffe", emoji = "🦒", color = 0xFFFBBF24L),
    AnimalSkinOption(id = "zebra", name = "Zebra", emoji = "🦓", color = 0xFFCBD5E1L),
    AnimalSkinOption(id = "unicorn", name = "Unicorn", emoji = "🦄", color = 0xFFFF4081L),
    AnimalSkinOption(id = "eagle", name = "Eagle", emoji = "🦅", color = 0xFFD97706L),
    AnimalSkinOption(id = "owl", name = "Owl", emoji = "🦉", color = 0xFF8B5CF6L),
    AnimalSkinOption(id = "flamingo", name = "Flamingo", emoji = "🦩", color = 0xFFF43F5EL),
    AnimalSkinOption(id = "peacock", name = "Peacock", emoji = "🦚", color = 0xFF0D9488L),
    AnimalSkinOption(id = "parrot", name = "Parrot", emoji = "🦜", color = 0xFF10B981L),
    AnimalSkinOption(id = "penguin", name = "Penguin", emoji = "🐧", color = 0xFF38BDF8L),
    AnimalSkinOption(id = "duck", name = "Duck", emoji = "🦆", color = 0xFF14B8A6L),
    AnimalSkinOption(id = "bat", name = "Bat", emoji = "🦇", color = 0xFF7C3AEDL),
    AnimalSkinOption(id = "butterfly", name = "Butterfly", emoji = "🦋", color = 0xFFA855F7L),
    AnimalSkinOption(id = "bee", name = "Bee", emoji = "🐝", color = 0xFFFACC15L),
    AnimalSkinOption(id = "frog", name = "Frog", emoji = "🐸", color = 0xFF00E676L),
    AnimalSkinOption(id = "snake", name = "Snake", emoji = "🐍", color = 0xFF059669L),
    AnimalSkinOption(id = "crocodile", name = "Croc", emoji = "🐊", color = 0xFF15803DL),
    AnimalSkinOption(id = "turtle", name = "Turtle", emoji = "🐢", color = 0xFF34D399L),
    AnimalSkinOption(id = "bunny", name = "Bunny", emoji = "🐰", color = 0xFFF472B6L),
    AnimalSkinOption(id = "cat", name = "Cat", emoji = "🐱", color = 0xFFFB923CL),
    AnimalSkinOption(id = "dog", name = "Dog", emoji = "🐶", color = 0xFFF59E0BL),
    AnimalSkinOption(id = "pig", name = "Pig", emoji = "🐷", color = 0xFFFF80ABL),
    AnimalSkinOption(id = "cow", name = "Cow", emoji = "🐮", color = 0xFF67E8F9L),
    AnimalSkinOption(id = "hedgehog", name = "Hedgehog", emoji = "🦔", color = 0xFFB45309L)
)

/**
 * Color option for customizing the player's background blob color.
 */
data class ColorOption(
    val id: String,
    val name: String,
    val color: Long
)

val COLOR_OPTIONS = listOf(
    ColorOption("cyan", "Cyan", 0xFF00E5FFL),
    ColorOption("ocean_blue", "Ocean Blue", 0xFF0284C7L),
    ColorOption("royal_blue", "Royal Blue", 0xFF3B82F6L),
    ColorOption("sky_blue", "Sky Blue", 0xFF38BDF8L),
    ColorOption("ice_blue", "Ice Blue", 0xFF67E8F9L),
    ColorOption("cyan_blue", "Cyan Blue", 0xFF06B6D4L),
    ColorOption("teal", "Teal", 0xFF0D9488L),
    ColorOption("aqua", "Aqua", 0xFF14B8A6L),
    ColorOption("mint", "Mint", 0xFF34D399L),
    ColorOption("emerald", "Emerald", 0xFF10B981L),
    ColorOption("neon_green", "Neon Green", 0xFF00E676L),
    ColorOption("green", "Green", 0xFF22C55EL),
    ColorOption("dark_green", "Dark Green", 0xFF15803DL),
    ColorOption("forest_green", "Forest Green", 0xFF059669L),
    ColorOption("lime", "Lime", 0xFF84CC16L),
    ColorOption("gold", "Gold", 0xFFFFD600L),
    ColorOption("electric_yellow", "Electric Yellow", 0xFFFACC15L),
    ColorOption("sun_yellow", "Sun Yellow", 0xFFFBBF24L),
    ColorOption("honey_gold", "Honey Gold", 0xFFFFB300L),
    ColorOption("amber", "Amber", 0xFFF59E0BL),
    ColorOption("bright_orange", "Bright Orange", 0xFFFF6D00L),
    ColorOption("sunset_orange", "Sunset Orange", 0xFFFF3D00L),
    ColorOption("peach", "Peach", 0xFFFB923CL),
    ColorOption("coral", "Coral", 0xFFFF5252L),
    ColorOption("crimson_red", "Crimson Red", 0xFFFF1744L),
    ColorOption("rose", "Rose", 0xFFF43F5EL),
    ColorOption("hot_pink", "Hot Pink", 0xFFFF4081L),
    ColorOption("candy_pink", "Candy Pink", 0xFFF472B6L),
    ColorOption("bubblegum", "Bubblegum", 0xFFFF80ABL),
    ColorOption("magenta", "Magenta", 0xFFD500F9L),
    ColorOption("fuchsia", "Fuchsia", 0xFFE040FBL),
    ColorOption("bright_purple", "Bright Purple", 0xFFA855F7L),
    ColorOption("violet", "Violet", 0xFF8B5CF6L),
    ColorOption("deep_violet", "Deep Violet", 0xFF7C3AEDL),
    ColorOption("lavender", "Lavender", 0xFFA78BFAL),
    ColorOption("indigo", "Indigo", 0xFF818CF8L),
    ColorOption("warm_brown", "Warm Brown", 0xFFA1887FL),
    ColorOption("bronze", "Bronze", 0xFFB45309L),
    ColorOption("rust", "Rust", 0xFFD97706L),
    ColorOption("stone", "Stone", 0xFF8E8D8AL),
    ColorOption("blue_grey", "Blue Grey", 0xFF78909CL),
    ColorOption("slate", "Slate", 0xFF64748BL),
    ColorOption("silver_slate", "Silver Slate", 0xFF94A3B8L),
    ColorOption("silver", "Silver", 0xFFCBD5E1L),
    ColorOption("white", "Pure White", 0xFFFFFFFFL),
    ColorOption("charcoal", "Charcoal", 0xFF374151L)
)

